import { ArrowLeftIcon, CopyIcon, PauseIcon, PlayIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  useGetSurveyDetails,
  useInvalidateSurveyDetails,
  useResumeSurvey,
  useStopSurvey,
} from '@/api/communication'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { prepareRecipients } from '@/components/Surveys/helpers'
import SurveyFormModal from '@/components/Surveys/SurveyFormModal'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Link } from '@/components/ui/link'
import { popupFactory, routerFactory, toastFactory } from '@/factories'
import { getSurveyStatusColor, SurveyStatusEnum, type SurveyType } from '@/models'
import { selectBuildingsForFilter } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'
import { getApiErrorMessage } from '@/utils/error'

import { prepareFrequency } from './survey-details.page.utils'
import { SurveyDetailsAnalytics } from './survey-details-analytics'
import { SurveyDetailsCharts } from './survey-details-charts'
import { SurveyDetailsPanel } from './survey-details-panel'
import { SurveyRecipientsTable } from './survey-recipients-table'

export const SurveyDetails = () => {
  const { t } = useTranslation(['communication'])
  const { useParams } = routerFactory.useRouter()
  const params = useParams<'/dashboard/survey/:id'>()
  const buildings = useSelector(selectBuildingsForFilter)
  const userRole = useSelector(selectUserRole)

  const surveyId = Number(params.id)
  const { data: details } = useGetSurveyDetails({ id: surveyId })
  const invalidateSurveyDetails = useInvalidateSurveyDetails({ id: surveyId })
  const { mutateAsync: stopSurveyMutation, isPending: isStoppingPending } = useStopSurvey()
  const { mutateAsync: resumeSurveyMutation, isPending: isResumingPending } = useResumeSurvey()

  const isLoading = isStoppingPending || isResumingPending

  const selectedBuilding =
    details && buildings.find((building) => building.id === details.buildingId)

  const handleStopSurvey = async () => {
    if (!details) return
    try {
      await stopSurveyMutation({ surveyId: details.id })
      await invalidateSurveyDetails()
      toastFactory.success(t('communication:surveyDetails.stopSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleResumeSurvey = async () => {
    if (!details) return
    try {
      await resumeSurveyMutation({ surveyId: details.id })
      await invalidateSurveyDetails()
      toastFactory.success(t('communication:surveyDetails.resumeSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleDuplicateSurvey = () => {
    // TODO: Refactor modal
    popupFactory.open(SurveyFormModal, {
      buildings,
      currentSurvey: details,
      isDuplicateForm: true,
      onSuccess: () => invalidateSurveyDetails(),
      selectedBuilding,
      userRole,
    })
  }

  const handleEditSurvey = () => {
    // TODO: Refactor modal
    popupFactory.open(SurveyFormModal, {
      buildings,
      currentSurvey: details,
      isDuplicateForm: false,
      onSuccess: () => invalidateSurveyDetails(),
      selectedBuilding,
      userRole,
    })
  }

  const showAnalytics = details?.status && details.status !== SurveyStatusEnum.enum.Scheduled
  const showStopResume = details?.status !== SurveyStatusEnum.enum.Completed
  const showEdit = details?.status === SurveyStatusEnum.enum.Scheduled

  const panelData = useMemo(() => {
    if (!details) return null

    return {
      communicationType: details.communicationType,
      questions: details.questions?.map((q) => q.title) ?? [],
      recipients: prepareRecipients(t, details.recipients),
      startAndEndDates: prepareFrequency(details, selectedBuilding?.timezone, t),
      surveyType: details.type as SurveyType,
      ...(details.subtype && { surveySubtype: details.subtype }),
    }
  }, [details, selectedBuilding, t])

  const isOnHold = details?.status === SurveyStatusEnum.enum['On hold']

  if (!details) return null

  return (
    // TODO: Remove survey-details className from here. It's added because survey-details is outside of /building-dashboard routes.
    <div className={`survey-details`}>
      <Page>
        <PageHeader
          loading={isLoading}
          title={t('communication:surveyDetails.title')}
          backButton={
            <Link navigateBack use="secondary" size="small" to={{ path: '/dashboard/survey' }}>
              <Icon icon={ArrowLeftIcon} />
              {t('communication:surveyDetails.back')}
            </Link>
          }
          badges={
            details?.status && (
              <Badge color={getSurveyStatusColor(details?.status)} size="small" variant="light">
                {details?.status}
              </Badge>
            )
          }
          actions={
            <>
              <Button
                use="secondary"
                size="small"
                disabled={isLoading}
                onClick={handleDuplicateSurvey}
              >
                <Icon icon={CopyIcon} />
                {t('communication:surveyDetails.duplicateSurvey')}
              </Button>
              {showStopResume && details?.status !== SurveyStatusEnum.enum.Completed && (
                <Button
                  use="secondary"
                  size="small"
                  loading={isLoading}
                  onClick={isOnHold ? handleResumeSurvey : handleStopSurvey}
                >
                  <Icon icon={isOnHold ? PlayIcon : PauseIcon} />
                  {isOnHold
                    ? t('communication:surveyDetails.resumeSurvey')
                    : t('communication:surveyDetails.stopSurvey')}
                </Button>
              )}
              {showEdit && details?.status === SurveyStatusEnum.enum.Scheduled && (
                <Button use="primary" size="small" disabled={isLoading} onClick={handleEditSurvey}>
                  {t('communication:surveyDetails.editSurvey')}
                </Button>
              )}
            </>
          }
        />
        {panelData && <SurveyDetailsPanel data={panelData} />}
        {showAnalytics ? (
          <>
            <SurveyDetailsAnalytics id={details.id} communicationType={details.communicationType} />
            <SurveyDetailsCharts subtype={details.subtype} questions={details.questions} />
          </>
        ) : null}
        {showAnalytics && (
          <SurveyRecipientsTable
            surveyId={details.id}
            communicationType={details.communicationType}
            questions={details.questions || []}
            buildingName={selectedBuilding?.name}
            timezone={selectedBuilding?.timezone}
          />
        )}
      </Page>
    </div>
  )
}
